import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import LangToggle from "./LangToggle";
import { LogOut, MonitorSmartphone } from "lucide-react";

export default function Navbar() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const navLinkClass = ({ isActive }) =>
    `px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
      isActive ? "bg-zinc-900 text-white" : "text-zinc-700 hover:text-zinc-900 hover:bg-zinc-900/5"
    }`;

  return (
    <header
      className={`sticky top-0 z-40 transition-all ${scrolled ? "bg-white/75 backdrop-blur-xl border-b border-zinc-200/80" : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
        <Link to="/" data-testid="navbar-brand" className="flex items-center gap-2.5 me-auto group">
          <span className="w-9 h-9 rounded-xl bg-zinc-900 grid place-items-center transition-transform group-hover:scale-105 shadow-sm">
            <MonitorSmartphone className="w-5 h-5 text-white" strokeWidth={2} />
          </span>
          <span dir="ltr" className="font-display font-bold text-base sm:text-lg tracking-tight inline-flex items-baseline">
            <span className="text-zinc-900">Trust</span>
            <span className="text-gradient">Con</span>
            <span className="text-zinc-900">App</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/" end data-testid="nav-home" className={navLinkClass}>{t("nav_home")}</NavLink>
          <NavLink to="/quick" data-testid="nav-quick" className={navLinkClass}>{t("nav_quick")}</NavLink>
          <NavLink to="/pricing" data-testid="nav-pricing" className={navLinkClass}>{t("nav_pricing")}</NavLink>
          {user && (
            <NavLink to="/dashboard" data-testid="nav-dashboard" className={navLinkClass}>{t("nav_dashboard")}</NavLink>
          )}
          {user && user.role === "admin" && (
            <NavLink to="/admin" data-testid="nav-admin" className={navLinkClass}>{t("nav_admin")}</NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <LangToggle />
          {user ? (
            <button
              onClick={handleLogout}
              data-testid="nav-logout"
              className="btn btn-secondary btn-sm"
            >
              <LogOut className="w-4 h-4" strokeWidth={2} />
              <span className="hidden sm:inline">{t("nav_logout")}</span>
            </button>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="btn btn-ghost btn-sm hidden sm:inline-flex">{t("nav_login")}</Link>
              <Link to="/register" data-testid="nav-register" className="btn btn-primary btn-sm">{t("nav_register")}</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
